import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, BACKEND_URL } from "../lib/api";
import { CREST_URL } from "../components/SiteLayout";
import { toast } from "sonner";
import { LogOut, RefreshCw, Trash2, Copy, Download, Plus, Search, CheckCircle2, AlertCircle, Clock, Eye, EyeOff } from "lucide-react";

const AdminMembersPage = () => {
  const { user, logout } = useAuth();
  const [members, setMembers] = useState([]);
  const [dues, setDues] = useState([]);
  const [settings, setSettings] = useState({ period_label: "", amount: 0, cashapp_handle: "" });
  const [editingSettings, setEditingSettings] = useState({ period_label: "", amount: 0, cashapp_handle: "" });
  const [savingSettings, setSavingSettings] = useState(false);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [editionFilter, setEditionFilter] = useState("all");
  const [showPins, setShowPins] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const nav = useNavigate();

  const load = async () => {
    try {
      const [m, d, s] = await Promise.all([
        api.get("/admin/members"),
        api.get("/admin/dues"),
        api.get("/admin/dues/settings"),
      ]);
      setMembers(m.data);
      setDues(d.data);
      setSettings(s.data);
      setEditingSettings(s.data);
    } catch {
      toast.error("Could not load members");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const editions = useMemo(() => {
    const s = new Set(members.map((m) => m.edition));
    return ["all", ...Array.from(s).sort().reverse()];
  }, [members]);

  const duesByMember = useMemo(() => {
    const m = {};
    dues.forEach((d) => { m[d.member_id] = d; });
    return m;
  }, [dues]);

  const filtered = useMemo(() => {
    return members.filter((m) => {
      if (editionFilter !== "all" && m.edition !== editionFilter) return false;
      if (!q) return true;
      return m.name.toLowerCase().includes(q.toLowerCase());
    });
  }, [members, q, editionFilter]);

  const handleLogout = async () => { await logout(); nav("/"); };

  const copyPin = (pin, name) => {
    navigator.clipboard.writeText(pin);
    toast.success(`Copied PIN for ${name}`);
  };

  const regenPin = async (id) => {
    if (!window.confirm("Generate a new PIN for this member? The old PIN will stop working immediately.")) return;
    try {
      const { data } = await api.patch(`/admin/members/${id}/regenerate-pin`);
      setMembers((arr) => arr.map((m) => (m.id === id ? data : m)));
      toast.success(`New PIN: ${data.pin}`);
    } catch { toast.error("Could not regenerate PIN"); }
  };

  const delMember = async (id, name) => {
    if (!window.confirm(`Delete ${name}? This also clears their dues history.`)) return;
    try {
      await api.delete(`/admin/members/${id}`);
      setMembers((arr) => arr.filter((m) => m.id !== id));
      toast.success("Member deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  const togglePaid = async (memberId) => {
    try {
      const { data } = await api.patch(`/admin/dues/${memberId}/toggle-paid`);
      setDues((arr) => {
        const has = arr.find((d) => d.member_id === memberId);
        if (has) return arr.map((d) => (d.member_id === memberId ? data : d));
        return [...arr, data];
      });
    } catch { toast.error("Could not update dues"); }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const payload = {
        period_label: editingSettings.period_label,
        amount: Number(editingSettings.amount) || 0,
        cashapp_handle: editingSettings.cashapp_handle,
      };
      const { data } = await api.put("/admin/dues/settings", payload);
      setSettings(data);
      toast.success("Dues settings saved");
      load();
    } catch { toast.error("Save failed"); }
    finally { setSavingSettings(false); }
  };

  const exportCsv = () => {
    const token = localStorage.getItem("godsc_token");
    fetch(`${BACKEND_URL}/api/admin/members/export-csv`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "godsc_members_pins.csv";
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        toast.success("CSV downloaded");
      })
      .catch(() => toast.error("CSV download failed"));
  };

  const printPinCards = () => {
    const w = window.open("", "_blank");
    if (!w) { toast.error("Allow pop-ups to print PIN cards"); return; }
    const cards = members
      .slice()
      .sort((a, b) => (a.edition + a.name).localeCompare(b.edition + b.name))
      .map((m) => `
        <div class="card">
          <div class="brand">GODSC &middot; ${m.edition} Edition</div>
          <div class="name">${m.name}</div>
          <div class="pin-label">Members Portal PIN</div>
          <div class="pin">${m.pin}</div>
          <div class="note">Sign in at <strong>${window.location.origin}/member-login</strong></div>
        </div>`).join("");
    w.document.write(`<!doctype html><html><head><title>GODSC PINs</title>
      <style>
        @page { margin: 0.5in; }
        body { font-family: Georgia, serif; padding: 0; margin: 0; color: #0A1F44; }
        .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; padding: 10px; }
        .card { border: 1.5px solid #0A1F44; padding: 18px 20px; page-break-inside: avoid; }
        .brand { font-size: 11px; letter-spacing: 0.22em; text-transform: uppercase; color: #C9A227; }
        .name { font-size: 22px; font-weight: 600; margin-top: 6px; }
        .pin-label { font-size: 10px; letter-spacing: 0.22em; text-transform: uppercase; color: #5C5C5C; margin-top: 18px; }
        .pin { font-size: 36px; letter-spacing: 0.4em; font-weight: 700; color: #D4AF37; margin-top: 4px; }
        .note { font-size: 10px; color: #5C5C5C; margin-top: 14px; }
      </style></head><body><div class="grid">${cards}</div>
      <script>window.onload = function(){ window.print(); };</script>
      </body></html>`);
    w.document.close();
  };

  return (
    <div className="admin-shell" data-testid="admin-members-page">
      <AdminTopbar user={user} onLogout={handleLogout} active="members" />

      <div className="max-w-[1400px] mx-auto px-6 py-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <h1 className="font-serif-display text-4xl text-[#0A1F44]">Members &amp; Dues</h1>
            <p className="text-[#5C5C5C] mt-2">
              Manage the active member roster, distribute PINs, and verify annual dues payments.
            </p>
          </div>
          <div className="flex gap-2">
            <button onClick={exportCsv} className="inline-flex items-center gap-2 px-4 py-2.5 border border-[#0A1F44] text-[#0A1F44] hover:bg-[#0A1F44] hover:text-[#D4AF37] transition-colors text-xs tracking-[0.22em] uppercase" data-testid="export-csv">
              <Download size={14} /> Export CSV
            </button>
            <button onClick={printPinCards} className="inline-flex items-center gap-2 px-4 py-2.5 border border-[#D4AF37] text-[#D4AF37] bg-[#0A1F44] hover:bg-[#D4AF37] hover:text-black transition-colors text-xs tracking-[0.22em] uppercase" data-testid="print-cards">
              Print PIN Cards
            </button>
          </div>
        </div>

        {/* Dues settings */}
        <div className="mt-8 bg-white border border-[#D8D3C4] p-6 md:p-8" data-testid="dues-settings">
          <div className="overline text-[#C9A227]">Current Dues Settings</div>
          <h2 className="font-serif-display text-2xl text-[#0A1F44] mt-1">Period &amp; Amount</h2>
          <div className="mt-5 grid sm:grid-cols-4 gap-4">
            <Field label="Period Label">
              <input value={editingSettings.period_label} onChange={(e) => setEditingSettings({ ...editingSettings, period_label: e.target.value })} className={inputCls} data-testid="settings-period" />
            </Field>
            <Field label="Amount ($)">
              <input type="number" min="0" step="1" value={editingSettings.amount} onChange={(e) => setEditingSettings({ ...editingSettings, amount: e.target.value })} className={inputCls} data-testid="settings-amount" />
            </Field>
            <Field label="Cash App Handle (no $)">
              <input value={editingSettings.cashapp_handle} onChange={(e) => setEditingSettings({ ...editingSettings, cashapp_handle: e.target.value })} className={inputCls} data-testid="settings-cashapp" />
            </Field>
            <div className="flex items-end">
              <button onClick={saveSettings} disabled={savingSettings} className="btn-primary-navy w-full disabled:opacity-60" data-testid="save-settings">
                {savingSettings ? "Saving..." : "Save Settings"}
              </button>
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className="mt-10 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex flex-wrap gap-2">
            <span className="text-[10px] tracking-[0.22em] uppercase text-[#5C5C5C] self-center mr-2">Edition:</span>
            {editions.map((e) => (
              <button
                key={e}
                onClick={() => setEditionFilter(e)}
                className={`px-3 py-2 text-[11px] tracking-[0.2em] uppercase border ${editionFilter === e ? "bg-[#0A1F44] text-white border-[#0A1F44]" : "bg-white text-[#0A1F44] border-[#D8D3C4] hover:border-[#0A1F44]"}`}
                data-testid={`members-filter-${e}`}
              >
                {e}
              </button>
            ))}
          </div>
          <div className="flex gap-2 items-center">
            <button onClick={() => setShowPins((s) => !s)} className="inline-flex items-center gap-2 px-3 py-2 border border-[#D8D3C4] text-[#0A1F44] text-xs tracking-[0.22em] uppercase hover:border-[#D4AF37]" data-testid="toggle-show-pins">
              {showPins ? <EyeOff size={14} /> : <Eye size={14} />}
              {showPins ? "Hide PINs" : "Show PINs"}
            </button>
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5C5C]" />
              <input placeholder="Search members..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 pr-3 py-2.5 w-full lg:w-72 border border-[#D8D3C4] bg-white text-sm focus:outline-none focus:border-[#D4AF37] rounded-none" data-testid="members-search" />
            </div>
            <button onClick={() => setAddOpen(true)} className="inline-flex items-center gap-2 btn-primary-navy" data-testid="open-add-member">
              <Plus size={14} /> Add
            </button>
            <button onClick={() => setBulkOpen(true)} className="inline-flex items-center gap-2 px-4 py-2.5 border border-[#D8D3C4] text-[#0A1F44] text-xs tracking-[0.22em] uppercase hover:border-[#D4AF37]" data-testid="open-bulk-paste">
              Bulk Paste
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="mt-6 border border-[#D8D3C4] bg-white overflow-x-auto">
          <table className="w-full text-sm" data-testid="members-table">
            <thead className="bg-[#F5F1E6] text-[#0A1F44]">
              <tr>
                <th className="text-left px-5 py-3 overline">Name</th>
                <th className="text-left px-5 py-3 overline">Edition</th>
                <th className="text-left px-5 py-3 overline">PIN</th>
                <th className="text-left px-5 py-3 overline">Dues ({settings.period_label})</th>
                <th className="text-right px-5 py-3 overline">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="px-5 py-10 text-center text-[#5C5C5C]">Loading...</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={5} className="px-5 py-10 text-center text-[#5C5C5C]" data-testid="members-empty">No members match your filters.</td></tr>
              ) : filtered.map((m) => {
                const d = duesByMember[m.id];
                return (
                  <tr key={m.id} className="border-t border-[#D8D3C4] hover:bg-[#FAF7ED]" data-testid={`member-row-${m.id}`}>
                    <td className="px-5 py-4 font-medium">{m.name}</td>
                    <td className="px-5 py-4 text-[#5C5C5C]">{m.edition}</td>
                    <td className="px-5 py-4">
                      <button onClick={() => copyPin(m.pin, m.name)} className="inline-flex items-center gap-1.5 font-mono text-[#0A1F44] hover:text-[#C9A227]" data-testid={`pin-${m.id}`}>
                        {showPins ? m.pin : "\u2022\u2022\u2022\u2022"} <Copy size={12} />
                      </button>
                    </td>
                    <td className="px-5 py-4">
                      <DuesBadge dues={d} />
                    </td>
                    <td className="px-5 py-4 text-right">
                      <div className="inline-flex items-center gap-2">
                        <button
                          onClick={() => togglePaid(m.id)}
                          className={`text-[11px] tracking-[0.18em] uppercase px-3 py-1.5 border ${d?.paid ? "border-green-700 text-green-700 hover:bg-green-50" : "border-[#D4AF37] text-[#D4AF37] bg-[#0A1F44] hover:bg-[#D4AF37] hover:text-black"}`}
                          data-testid={`toggle-paid-${m.id}`}
                        >
                          {d?.paid ? "Mark Unpaid" : "Mark Paid"}
                        </button>
                        <button onClick={() => regenPin(m.id)} className="text-[#0A1F44] hover:text-[#C9A227]" data-testid={`regen-${m.id}`} aria-label="Regenerate PIN">
                          <RefreshCw size={15} />
                        </button>
                        <button onClick={() => delMember(m.id, m.name)} className="text-red-600 hover:text-red-800" data-testid={`del-member-${m.id}`} aria-label="Delete">
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {addOpen && <AddMemberModal onClose={() => setAddOpen(false)} onAdded={load} />}
      {bulkOpen && <BulkPasteModal onClose={() => setBulkOpen(false)} onAdded={load} />}
    </div>
  );
};

const AddMemberModal = ({ onClose, onAdded }) => {
  const [name, setName] = useState("");
  const [edition, setEdition] = useState("40th");
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/admin/members", { name, edition });
      toast.success(`Added ${data.name}. PIN: ${data.pin}`);
      onAdded();
      onClose();
    } catch { toast.error("Could not add member"); }
    finally { setSaving(false); }
  };
  return (
    <ModalShell title="Add Member" onClose={onClose} testid="add-member-modal">
      <form onSubmit={submit} className="space-y-4">
        <Field label="Full Name">
          <input required value={name} onChange={(e) => setName(e.target.value)} className={inputCls} data-testid="add-member-name" />
        </Field>
        <Field label="Edition">
          <input required value={edition} onChange={(e) => setEdition(e.target.value)} className={inputCls} data-testid="add-member-edition" placeholder="e.g. 40th" />
        </Field>
        <div className="flex gap-3 pt-2">
          <button type="submit" disabled={saving} className="btn-primary-navy disabled:opacity-60" data-testid="add-member-submit">
            {saving ? "Adding..." : "Add Member"}
          </button>
          <button type="button" onClick={onClose} className="btn-outline-navy">Cancel</button>
        </div>
      </form>
    </ModalShell>
  );
};

const BulkPasteModal = ({ onClose, onAdded }) => {
  const [text, setText] = useState("");
  const [edition, setEdition] = useState("40th");
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    const names = text.split("\n").map((n) => n.trim()).filter(Boolean);
    if (names.length === 0) { toast.error("Paste at least one name"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/admin/members/bulk-paste", { names, edition });
      toast.success(`Added ${data.added}, skipped ${data.skipped_duplicates} duplicate(s)`);
      onAdded();
      onClose();
    } catch { toast.error("Bulk add failed"); }
    finally { setSaving(false); }
  };
  return (
    <ModalShell title="Bulk Add Members" onClose={onClose} testid="bulk-modal">
      <form onSubmit={submit} className="space-y-4">
        <Field label="Edition (applied to all)">
          <input required value={edition} onChange={(e) => setEdition(e.target.value)} className={inputCls} data-testid="bulk-edition" />
        </Field>
        <Field label="Names (one per line)">
          <textarea rows={10} value={text} onChange={(e) => setText(e.target.value)} className={inputCls} data-testid="bulk-names" placeholder={`Jane Doe\nJohn Smith\n...`} />
        </Field>
        <div className="flex gap-3 pt-2">
          <button type="submit" disabled={saving} className="btn-primary-navy disabled:opacity-60" data-testid="bulk-submit">
            {saving ? "Adding..." : "Add All"}
          </button>
          <button type="button" onClick={onClose} className="btn-outline-navy">Cancel</button>
        </div>
      </form>
    </ModalShell>
  );
};

const ModalShell = ({ title, onClose, children, testid }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" data-testid={testid}>
    <div className="bg-white border border-[#D8D3C4] w-full max-w-lg">
      <div className="px-6 py-4 bg-[#0A1F44] text-white flex items-center justify-between">
        <div className="font-serif-display text-xl">{title}</div>
        <button onClick={onClose} className="text-[#D4AF37] hover:text-white text-xs tracking-[0.22em] uppercase">Close</button>
      </div>
      <div className="p-6">{children}</div>
    </div>
  </div>
);

const DuesBadge = ({ dues }) => {
  if (dues?.paid) {
    return <span className="inline-flex items-center gap-1.5 px-2 py-1 border border-green-700 text-green-700 bg-green-50 text-[11px] tracking-[0.18em] uppercase"><CheckCircle2 size={13} /> Paid</span>;
  }
  if (dues?.payment_clicked_at) {
    return <span className="inline-flex items-center gap-1.5 px-2 py-1 border border-[#D4AF37] text-[#8A6E14] bg-[#FBF6E4] text-[11px] tracking-[0.18em] uppercase"><Clock size={13} /> Clicked</span>;
  }
  return <span className="inline-flex items-center gap-1.5 px-2 py-1 border border-red-400 text-red-700 bg-red-50 text-[11px] tracking-[0.18em] uppercase"><AlertCircle size={13} /> Unpaid</span>;
};

export const AdminTopbar = ({ user, onLogout, active }) => (
  <div className="bg-[#0A1F44] text-white">
    <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center justify-between flex-wrap gap-4">
      <Link to="/admin" className="flex items-center gap-3" data-testid="admin-logo">
        <img src={CREST_URL} alt="GODSC" className="h-10 w-10 object-contain" />
        <div>
          <div className="font-serif-display text-xl leading-none">GODSC Admin</div>
          <div className="text-[10px] tracking-[0.28em] uppercase text-[#D4AF37] mt-1">Executive Portal</div>
        </div>
      </Link>
      <nav className="flex items-center gap-1">
        <Link to="/admin" className={`px-4 py-2 text-xs tracking-[0.22em] uppercase border ${active === "submissions" ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-transparent text-[#D8D3C4] hover:text-[#D4AF37]"}`} data-testid="tab-submissions">
          Submissions
        </Link>
        <Link to="/admin/members" className={`px-4 py-2 text-xs tracking-[0.22em] uppercase border ${active === "members" ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-transparent text-[#D8D3C4] hover:text-[#D4AF37]"}`} data-testid="tab-members">
          Members &amp; Dues
        </Link>
      </nav>
      <div className="flex items-center gap-4 text-sm">
        <Link to="/" className="hover:text-[#D4AF37]" data-testid="view-site">View Site</Link>
        <span className="text-[#D4AF37]">{user?.username}</span>
        <button onClick={onLogout} className="inline-flex items-center gap-2 px-3 py-2 border border-[#D4AF37] text-[#D4AF37] text-xs tracking-[0.22em] uppercase hover:bg-[#D4AF37] hover:text-black transition-colors" data-testid="admin-logout">
          <LogOut size={14} /> Sign Out
        </button>
      </div>
    </div>
  </div>
);

const inputCls =
  "w-full border border-[#D8D3C4] bg-white px-3 py-3 text-[#0A1F44] focus:outline-none focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/30 rounded-none font-sans-body text-sm";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="overline text-[#5C5C5C] mb-2 block">{label}</span>
    {children}
  </label>
);

export default AdminMembersPage;
